import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { z } from "zod";

import {
  CHART_DISPLAY_TYPES,
  NUMERIC_AGGREGATIONS,
  Period,
  PERIODS,
} from "@/shared/domain/metrics";
import { savedFiltersSchema } from "@/shared/domain/views/savedFilters";

/**
 * The persisted `dashboard_definition` schema: the types stored in a
 * `DashboardRecord`'s JSONB, the enums those types range over, and the
 * structural validator (`isDashboardDefinition`) that gates anything loaded
 * from outside the running app. Layout and URL handling live in the
 * MetricsDashboard feature; this module imports only from other shared
 * modules, so shared code never depends on a feature.
 */

// Required to validate a custom string
dayjs.extend(customParseFormat);

/** The dashboard_definition schema version. */
export const DASHBOARD_SCHEMA_VERSION = 1;

/**
 * The preset time windows a dashboard can aggregate over. Part of the
 * persisted schema: a stored definition's `range` is either one of these
 * values or a {@link CustomRange}.
 *
 * `short` is the abbreviated label for the segmented range control, which has
 * far less room than the menu the full `label` was written for.
 */
export const TIME_RANGES = [
  { value: "30d", label: "Last 30 days", short: "30D", days: 30 },
  { value: "90d", label: "Last 90 days", short: "3M", days: 90 },
  { value: "365d", label: "Last year", short: "1Y", days: 365 },
  { value: "all", label: "All time", short: "All", days: 3650 },
] as const;

export type PresetRange = (typeof TIME_RANGES)[number]["value"];

/**
 * An explicit window the user picked, as two INCLUSIVE calendar dates.
 *
 * Dates, not instants: no aggregation period is finer than a calendar day
 * (see `AggregationPeriod`), so a time of day could not change any result.
 * The stored values are exactly what the user chose. `TimeRange.fromDates`
 * adds the `+1 day` that makes the end exclusive when resolving, and never
 * writes it back here.
 */
export interface CustomRange {
  kind: "custom";
  /** `YYYY-MM-DD`, inclusive. */
  start: string;
  /** `YYYY-MM-DD`, inclusive. */
  end: string;
}

/**
 * A preset token or an explicit window. Modelled as a union rather than a
 * sentinel plus optional sibling fields so that a custom range without bounds
 * is unrepresentable, and so selecting a preset structurally discards any
 * previous bounds — the two are mutually exclusive by construction.
 */
export type TimeRangeMode = PresetRange | CustomRange;

export type CardKind = "trend" | "chart";

/** The wire format for a {@link CustomRange} bound. */
const ISO_DATE = "YYYY-MM-DD";

/** Whether `value` is a real `YYYY-MM-DD` calendar date. */
function isCalendarDate(value: string): boolean {
  return dayjs(value, ISO_DATE, true).isValid();
}

const presetRangeSchema = z.enum(TIME_RANGES.map((range) => range.value));

/**
 * Bounds are compared lexicographically, which is ordering-correct for
 * zero-padded ISO dates; `start === end` is a legal one-day window.
 */
const customRangeSchema = z
  .object({
    kind: z.literal("custom"),
    start: z.string().refine(isCalendarDate, `must be ${ISO_DATE}`),
    end: z.string().refine(isCalendarDate, `must be ${ISO_DATE}`),
  })
  .refine((range) => range.start <= range.end, "start must not follow end");

const timeRangeModeSchema = z.union([presetRangeSchema, customRangeSchema]);

/**
 * Parse an untrusted value — a URL param, a stored definition's `range` — into
 * a {@link TimeRangeMode}, or `null` if it is neither a known preset nor a
 * well-formed custom window.
 *
 * Takes `unknown` because the persisted value is no longer always a string.
 */
export function asTimeRangeMode(value: unknown): TimeRangeMode | null {
  const parsed = timeRangeModeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * How many slots the chart series palette has. One palette serves both the
 * card's colour picker and the series a `group_by` splits a chart into.
 *
 * A card persists a SLOT, not a colour, so the ORDER in `seriesColors.css` is
 * part of the stored contract: restyling a slot is free, reordering repaints
 * every saved dashboard, and SHRINKING the palette rejects cards holding a
 * slot that no longer exists (`normalizeCard` parses rather than strips), so
 * it needs a migration. A card with no colour draws in `--primary-main`.
 */
export const SERIES_PALETTE_SIZE = 10;

/** A slot in the series palette: `0` to `SERIES_PALETTE_SIZE - 1`. */
export type SeriesColor = number;

const seriesColorSchema = z
  .int()
  .min(0)
  .max(SERIES_PALETTE_SIZE - 1);

/**
 * Placement cells (and spans) must be sane positive integers. The cap guards
 * the layout-packing loops in `placedLayout.ts`, which iterate over every
 * occupied cell — a definition is rejected outright rather than accepted
 * with a span that could hang the packer.
 */
const MAX_PLACEMENT_CELLS = 1000;

const placementCellSchema = z.int().min(1).max(MAX_PLACEMENT_CELLS);

const placementSchema = z.object({
  row: placementCellSchema,
  col: placementCellSchema,
  w: placementCellSchema,
  h: placementCellSchema,
});

/** Fields shared by every card kind: the section it belongs to (missing means
 *  the first section) and its explicit placement (missing means auto-flow).
 *
 *  Spread LAST into every card schema, so the parsed key order — which the
 *  dashboard's stringify dirty check compares — is the same for every kind. */
const placedCardFields = {
  placement: placementSchema.optional(),
  section: z.string().optional(),
};

/**
 * A title the user gave the card, shown in place of the one generated from
 * its metric and aggregation. Absent means generated, which is what every card
 * saved before this field existed carries, so those dashboards stay
 * byte-identical through the dirty check.
 *
 * Any string is accepted, a blank one included: rejecting it would fail the
 * whole definition over a cosmetic field. A blank title renders as the
 * generated one (see `getCardTitle`).
 */
const cardTitleSchema = z.string().optional();

/**
 * The CANONICAL group-by paths the dashboard's Group by picker can produce:
 * the device id, and a device's or session's custom field.
 *
 * Canonical, and only canonical. Group-by resolution on the backend does NOT
 * go through the RoboQL catalog, so it performs no alias resolution: the
 * catalog treats `device.id` as a synonym of `device.device_id` for
 * FILTERING, but the aggregate endpoint rejects it. Accepting an alias here
 * would let a definition validate on the client and then 400 on every one of
 * that card's requests.
 *
 * The custom-field segment mirrors the backend's own
 * `CUSTOM_FIELD_NAME_PATTERN` (`[a-z][a-z0-9_]{0,62}`).
 */
const GROUP_BY_PATH =
  /^(device\.device_id|(device|session)\.custom\.[a-z][a-z0-9_]{0,62})$/;

/**
 * Split the chart into one series per distinct value of this field. Absent
 * means one series over everything the card's query matches.
 */
export const groupBySchema = z.string().regex(GROUP_BY_PATH);

const aggregationSchema = z.enum(NUMERIC_AGGREGATIONS);
const displaySchema = z.enum(
  CHART_DISPLAY_TYPES.map((display) => display.value),
);

/** A single-metric KPI card (a 1×1 "trend tile"). Deliberately has NO colour:
 *  colour is a chart-series concern. */
const trendCardSchema = z.object({
  id: z.string(),
  kind: z.literal("trend"),
  /** Null means "fall back to an available metric definition". */
  metric: z.string().nullable(),
  title: cardTitleSchema,
  aggregation: aggregationSchema,
  ...placedCardFields,
});

/**
 * A chart card that plots a `NumericAggregation` of its metric within each
 * bucket of the dashboard's `Period`.
 */
const aggregatedChartCardSchema = z.object({
  id: z.string(),
  kind: z.literal("chart"),
  /** Null means "fall back to the first available metric definition". */
  metric: z.string().nullable(),
  title: cardTitleSchema,
  aggregation: aggregationSchema,
  display: displaySchema,
  group_by: groupBySchema.optional(),
  color: seriesColorSchema.optional(),
  ...placedCardFields,
});

/**
 * A chart card that plots the metric's own values — one point per session —
 * over the dashboard's time range, unbucketed.
 *
 * It carries NO `aggregation` — there is none to apply — and that absence is
 * the discriminant between the two chart variants: `z.undefined().optional()`
 * infers the `?: undefined` declaration that lets TypeScript narrow in BOTH
 * directions off `card.aggregation === undefined`, and rejects a card that
 * names one.
 *
 * Its `display` is optional, and ABSENT means `line`: that is what every raw
 * card drew before the field existed here, and what the picker still leaves
 * unwritten, so a dashboard saved back then stays byte-identical through the
 * dirty check. Which displays it is OFFERED is narrower than what this
 * accepts — see `CHART_DISPLAY_TYPES.linearAxis`; a `bar` that arrives anyway
 * is drawn as a line rather than as the hairlines Chart.js would size for it
 * on a time-proportional axis.
 */
const rawChartCardSchema = z.object({
  id: z.string(),
  kind: z.literal("chart"),
  /** Null means "fall back to the first available metric definition". */
  metric: z.string().nullable(),
  title: cardTitleSchema,
  aggregation: z.undefined().optional(),
  display: displaySchema.optional(),
  group_by: groupBySchema.optional(),
  color: seriesColorSchema.optional(),
  ...placedCardFields,
});

/**
 * Any card a dashboard can hold. A chart naming an aggregation this app cannot
 * honour matches neither chart variant and is rejected, rather than silently
 * degrading to raw.
 */
export const cardSchema = z.union([
  trendCardSchema,
  aggregatedChartCardSchema,
  rawChartCardSchema,
]);

const sectionSchema = z.object({ id: z.string(), title: z.string() });

/**
 * The persisted `dashboard_definition` shape.
 *
 * `z.object` (not `z.strictObject`): unknown keys are STRIPPED on parse rather
 * than rejected, so a record written by a newer or older client still loads.
 * `normalizeDefinition` relies on that stripping to drop unknown fields.
 */
export const dashboardDefinitionSchema = z.object({
  schema_version: z.literal(DASHBOARD_SCHEMA_VERSION),
  range: timeRangeModeSchema,
  period: z.enum(PERIODS),
  /** Narrows every card's query. Absent means unfiltered. */
  filters: savedFiltersSchema.optional(),
  sections: z.array(sectionSchema),
  cards: z.array(cardSchema),
});

export type Placement = z.infer<typeof placementSchema>;
export type SectionConfig = z.infer<typeof sectionSchema>;
export type TrendCardConfig = z.infer<typeof trendCardSchema>;
export type AggregatedChartCardConfig = z.infer<
  typeof aggregatedChartCardSchema
>;
export type RawChartCardConfig = z.infer<typeof rawChartCardSchema>;
export type ChartCardConfig = AggregatedChartCardConfig | RawChartCardConfig;
export type CardConfig = z.infer<typeof cardSchema>;
export type DashboardDefinition = z.infer<typeof dashboardDefinitionSchema>;

/** Fields shared by every card kind. */
export type PlacedCardBase = Pick<CardConfig, "placement" | "section">;

/**
 * Strict structural check for a {@link DashboardDefinition} loaded from
 * outside the running app (a backend record's JSONB, a localStorage draft).
 * Only the current schema shape passes — including its `range`/`period`
 * enums, filter shape, card kinds, and placement bounds — so accepted input
 * can never reach a code path that breaks after acceptance, and rejected
 * input takes the caller's invalid-input fallback instead of loading
 * half-broken (e.g. an unknown range that would read back as a
 * permanently-dirty dashboard).
 */
export function isDashboardDefinition(
  value: unknown,
): value is DashboardDefinition {
  return dashboardDefinitionSchema.safeParse(value).success;
}

export function isPresetRange(range: TimeRangeMode): range is PresetRange {
  return typeof range === "string";
}

export function isCustomRange(range: TimeRangeMode): range is CustomRange {
  return typeof range !== "string";
}

export function asPeriod(value: string | null): Period | null {
  return value !== null && (PERIODS as readonly string[]).includes(value)
    ? (value as Period)
    : null;
}
